/**
 * Position-level TP/SL orchestration: turns the modal's desired end state
 * (a take-profit price and/or a stop-loss price) into executor calls.
 *
 * The executor groups legs at CREATE time (OCO), so any edit is expressed as
 * "cancel every existing leg, then create the desired set in one POST".
 * Live legs come from `selectTpslTriggers` (executor list first, position
 * enrichment as a fallback) — see that module for why `position.triggers`
 * alone is not trustworthy.
 */
import { useCallback, useContext, useEffect, useRef } from "react";
import {
  GetAccount200ResponsePositionsInner,
  GetMarkets200Response,
  MarginTypeResponse,
  SideResponse,
} from "../../codegen-api";
import { getMarketAddress } from "../../config/chains";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { truncateDecimalString } from "../../utils/decimalString";
import { floorToStepString } from "../../utils/format";
import { getContractPriceStep, IContractPriceStep } from "../../utils/instruments";
import { normalizeMarginType } from "../../utils/margin";
import { TpslLegsRemovedError } from "./tpsl.errors";
import { selectTpslTriggers, TpslTriggerView } from "./tpsl.selectors";
import { toSizeInTokensString, toTriggerPriceString } from "./tpsl.signing";
import {
  DEFAULT_TPSL_PRICE_SOURCE,
  TpslDesiredState,
  TpslLeg,
  TpslMarginMode,
  TpslOrder,
  TpslPriceSource,
} from "./tpsl.types";
import { useTpslApi } from "./useTpslApi";

/** Market orders are used at trigger time; 1% guards against thin books. */
export const DEFAULT_TPSL_SLIPPAGE_BPS = 100;

/** "0" tells the executor to close whatever size the position holds. */
const CLOSE_FULL_POSITION = "0";

const toPrice = (value?: number | string): number | undefined => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

/** The listed perp market's steps, or `undefined` while markets are unknown. */
export const findPerpContractStep = (
  markets: GetMarkets200Response[] | undefined,
  instrumentName: string | undefined
): IContractPriceStep | undefined => {
  if (!instrumentName) return undefined;
  const market = markets?.find((m) => m.instrument_name === instrumentName);
  return market ? getContractPriceStep(market) : undefined;
};

/**
 * The close size a leg is signed with, as plain decimal text floored onto
 * `amount_step`, or `undefined` when no size was given ("close it all", as
 * before).
 *
 * A size at or above what is held is sent as the position's own exact text:
 * still an explicit size, as before, but never more than the position, and
 * never through a float (a round trip of an 18-decimal size can land a wei above
 * it). A size that floors to nothing throws — sending "0" for it would silently
 * close everything.
 */
export const resolveCloseSize = (
  sizeInTokens: number | string | undefined,
  positionAmount: number | string | undefined,
  step?: Pick<IContractPriceStep, "amount_step" | "amount_precision">
): string | undefined => {
  const size = Number(sizeInTokens);
  if (!Number.isFinite(size) || size <= 0) return undefined;
  const heldText = truncateDecimalString(positionAmount, 18)?.replace(/^-/, "");
  if (heldText && Number(heldText) > 0 && size >= Number(heldText)) {
    return heldText;
  }

  const sized =
    step && step.amount_step > 0
      ? floorToStepString(sizeInTokens as number | string, step.amount_step, step.amount_precision)
      : truncateDecimalString(sizeInTokens, 18);
  if (!sized || !(Number(sized) > 0)) {
    throw new Error("Close size is smaller than this market's minimum size step.");
  }
  return sized;
};

/** `undefined` and `""`/`"0"` all mean "no value" on both sides. */
const sameOptionalNumber = (
  existing?: string,
  desired?: number | string
): boolean => {
  const left = Number(existing || 0);
  const right = Number(desired || 0);
  return (Number.isFinite(left) ? left : 0) === (Number.isFinite(right) ? right : 0);
};

/**
 * A leg is unchanged only when EVERY signed field matches — a price-only
 * comparison would silently swallow an edit that just flips the trigger source,
 * the close size or the limit price.
 */
const sameLeg = (
  existing: TpslTriggerView | undefined,
  desired: number | undefined,
  advanced: {
    priceSource: TpslPriceSource;
    sizeInTokens?: number | string;
    limitPrice?: number;
  }
): boolean => {
  if (!existing && desired == null) return true;
  if (!existing || desired == null) return false;
  return (
    Number(existing.trigger) === desired &&
    (existing.priceSource || DEFAULT_TPSL_PRICE_SOURCE) === advanced.priceSource &&
    sameOptionalNumber(existing.sizeInTokens, advanced.sizeInTokens) &&
    sameOptionalNumber(existing.limitPrice, advanced.limitPrice)
  );
};

export function positionMarginMode(
  position: GetAccount200ResponsePositionsInner
): TpslMarginMode {
  return normalizeMarginType(position.margin_type) === MarginTypeResponse.Isolated
    ? "isolated"
    : "cross";
}

export interface BuildLegOptions {
  /**
   * Human base-asset amount, already step-floored (see `resolveCloseSize`).
   * Omitted / non-positive means "close it all".
   */
  sizeInTokens?: number | string;
  priceSource?: TpslPriceSource;
  /** Human execution bound. Omitted means a pure market close at trigger. */
  limitPrice?: number;
}

export const buildLeg = (
  triggerType: TpslLeg["triggerType"],
  price: number,
  options: BuildLegOptions = {}
): TpslLeg => {
  const size =
    options.sizeInTokens !== undefined &&
    Number.isFinite(Number(options.sizeInTokens)) &&
    Number(options.sizeInTokens) > 0
      ? toSizeInTokensString(options.sizeInTokens)
      : CLOSE_FULL_POSITION;

  const leg: TpslLeg = {
    triggerType,
    triggerPrice: toTriggerPriceString(price),
    sizeInTokensToClose: size,
    slippageBps: DEFAULT_TPSL_SLIPPAGE_BPS,
    // Always sent, so every leg is leaf-v2 signed.
    priceSource: options.priceSource || DEFAULT_TPSL_PRICE_SOURCE,
  };

  if (Number.isFinite(options.limitPrice) && Number(options.limitPrice) > 0) {
    leg.limitPrice = toTriggerPriceString(options.limitPrice as number);
  }
  return leg;
};

export function useTpslForPosition() {
  const { supported, openOrders, refreshOrders, createTriggers, cancelTrigger } =
    useTpslApi();
  const { activePerpMarkets } = useContext(MarketInstrumentContext);

  // Read through a ref, not the closure. The "Set TP/SL again" action in the
  // error dialog holds the `submitTpslForPosition` from the render that failed,
  // and by then its `openOrders` still lists the legs this function had just
  // cancelled. Retrying against that snapshot re-cancels dead order ids: each
  // one prompts for a signature and then rethrows from `cancelTrigger`, outside
  // the create try/catch -- so the retry could never reach `createTriggers`,
  // and failed with a plain toast instead of offering itself again.
  const openOrdersRef = useRef(openOrders);
  useEffect(() => {
    openOrdersRef.current = openOrders;
  }, [openOrders]);

  const submitTpslForPosition = useCallback(
    async (
      position: GetAccount200ResponsePositionsInner | undefined,
      desired: TpslDesiredState
    ): Promise<TpslOrder[]> => {
      if (!position) {
        return [];
      }
      if (!supported) {
        throw new Error("TP/SL is not available on this network.");
      }

      const market = getMarketAddress(position.instrument_name);
      if (!market) {
        throw new Error(
          `No market address configured for ${position.instrument_name}.`
        );
      }

      const live = selectTpslTriggers(openOrdersRef.current, position);
      const nextTp = toPrice(desired.takeProfitPrice);
      const nextSl = toPrice(desired.stopLossPrice);
      const priceSource = desired.priceSource || DEFAULT_TPSL_PRICE_SOURCE;
      // Resolved before any cancel, so a size that cannot be signed never
      // tears down the live legs first.
      const sizeInTokens = resolveCloseSize(
        desired.sizeInTokens,
        position.amount,
        findPerpContractStep(activePerpMarkets, position.instrument_name)
      );
      const tpLimit = toPrice(desired.takeProfitLimitPrice);
      const slLimit = toPrice(desired.stopLossLimitPrice);

      // Nothing to do — leave the live triggers alone rather than
      // cancel/recreate them (which would burn a signature and a nonce).
      if (
        sameLeg(live.take_profit, nextTp, {
          priceSource,
          sizeInTokens,
          limitPrice: tpLimit,
        }) &&
        sameLeg(live.stop_loss, nextSl, {
          priceSource,
          sizeInTokens,
          limitPrice: slLimit,
        })
      ) {
        return [];
      }

      // Cancel first so a rejected create can never leave two live TP legs.
      const toCancel = [live.take_profit?.order_id, live.stop_loss?.order_id]
        .filter(Boolean) as string[];
      // eslint-disable-next-line no-restricted-syntax
      for (const triggerId of toCancel) {
        // Sequential: each cancel consumes a nonce from the executor.
        // eslint-disable-next-line no-await-in-loop
        await cancelTrigger(triggerId);
      }

      const legs: TpslLeg[] = [];
      if (nextTp != null) {
        legs.push(
          buildLeg("take_profit", nextTp, {
            sizeInTokens,
            priceSource,
            limitPrice: tpLimit,
          })
        );
      }
      if (nextSl != null) {
        legs.push(
          buildLeg("stop_loss", nextSl, {
            sizeInTokens,
            priceSource,
            limitPrice: slLimit,
          })
        );
      }

      if (!legs.length) {
        await refreshOrders();
        return [];
      }

      let created: TpslOrder[];
      try {
        created = await createTriggers({
          market,
          isLong: position.side === SideResponse.Buy,
          marginMode: positionMarginMode(position),
          // OCO only makes sense when both legs exist: filling one cancels the other.
          ocoLinked: legs.length === 2,
          legs,
        });
      } catch (error) {
        // The old legs are already gone: say so, rather than a plain failure.
        if (!toCancel.length) throw error;
        await refreshOrders().catch(() => undefined);
        throw new TpslLegsRemovedError(toCancel.length, error);
      }
      await refreshOrders();
      return created;
    },
    [
      activePerpMarkets,
      cancelTrigger,
      createTriggers,
      refreshOrders,
      supported,
    ]
  );

  return { supported, openOrders, submitTpslForPosition };
}
